import React from 'react';

/**
 * Extra line-drawing primitives for the shower schematics, on top of
 * `BathFixtureFrame.jsx`. Shower hardware is mostly pipe at an angle, so
 * these draw THICK SEGMENTS and TILTED RECTANGLES as polygons, and a
 * tilted disc's silhouette as an ellipse — none of which the rect / line /
 * circle set can express.
 *
 * Every helper takes local 2D points and a `map` that projects them into
 * the SVG viewBox (the schematics pass the front or the side projection),
 * so the drawing code can work in the same object-local frame as
 * `showerLayout.js`.
 */

const strokeProps = (strokeColor, strokeWidth, fill = 'none') => ({
  fill,
  stroke: strokeColor,
  strokeWidth,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  vectorEffect: 'non-scaling-stroke',
});

export function Poly({ points, strokeColor, strokeWidth, fill }) {
  return (
    <polygon
      points={points.map(([x, y]) => `${x},${y}`).join(' ')}
      {...strokeProps(strokeColor, strokeWidth, fill)}
    />
  );
}

export function Ellipse({ cx, cy, rx, ry, strokeColor, strokeWidth, fill }) {
  return (
    <ellipse
      cx={cx}
      cy={cy}
      rx={rx}
      ry={ry}
      {...strokeProps(strokeColor, strokeWidth, fill)}
    />
  );
}

/** Corners of a rectangle centred at `c`, `u` the unit direction of its length. */
export function boxCorners(c, u, halfAlong, halfAcross) {
  const n = [-u[1], u[0]];
  const corner = (sa, sc) => [
    c[0] + u[0] * halfAlong * sa + n[0] * halfAcross * sc,
    c[1] + u[1] * halfAlong * sa + n[1] * halfAcross * sc,
  ];
  return [corner(1, 1), corner(1, -1), corner(-1, -1), corner(-1, 1)];
}

/** A pipe of half-width `r` from `from` to `to` (local coords), as an outline. */
export function Bar({ from, to, r, map, strokeColor, strokeWidth }) {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy);
  const u = len > 1e-9 ? [dx / len, dy / len] : [0, 1];
  const c = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
  return (
    <Poly
      points={boxCorners(c, u, len / 2, r).map(map)}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

/** Rectangle centred at `centre` with its length along unit `dir`. */
export function TiltedRect({
  centre,
  dir,
  halfAlong,
  halfAcross,
  map,
  strokeColor,
  strokeWidth,
}) {
  return (
    <Poly
      points={boxCorners(centre, dir, halfAlong, halfAcross).map(map)}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}
